import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { T } from "../lib/fc3i18n";
import "./fc-premium.css";

/* Loads the display serif once (Google Fonts). */
export function usePremiumFonts() {
  useEffect(() => {
    if (document.getElementById("fp-fonts")) return;
    const l = document.createElement("link");
    l.id = "fp-fonts";
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap";
    document.head.appendChild(l);
  }, []);
}

/* Pointer + scroll driven 3D tilt. Writes --rx / --ry on the element. */
export function useTilt(ref, { baseX = 8, baseY = -18, ampX = 7, ampY = 12 } = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let tx = 0, ty = 0, cx = 0, cy = 0, raf = 0;
    const onMove = (e) => { tx = (e.clientX / window.innerWidth) * 2 - 1; ty = (e.clientY / window.innerHeight) * 2 - 1; };
    const loop = () => {
      cx += (tx - cx) * 0.06; cy += (ty - cy) * 0.06;
      el.style.setProperty("--ry", `${(baseY + cx * ampY).toFixed(2)}deg`);
      el.style.setProperty("--rx", `${(baseX - cy * ampX).toFixed(2)}deg`);
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("pointermove", onMove); };
  }, [ref, baseX, baseY, ampX, ampY]);
}

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
);

function MiniRing() {
  return (
    <svg className="fp-mring" viewBox="0 0 48 48" aria-hidden="true">
      <circle cx="24" cy="24" r="19" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="5" />
      <circle cx="24" cy="24" r="19" fill="none" stroke="url(#fpmr)" strokeWidth="5" strokeLinecap="round" strokeDasharray="78 120" transform="rotate(-90 24 24)" />
      <defs><linearGradient id="fpmr" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#F6E7D3" /><stop offset="1" stopColor="#F0457F" /></linearGradient></defs>
    </svg>
  );
}

/**
 * Cinematic left panel for Sign in / Sign up. Pure CSS 3D: a FemCare
 * screen in a phone, an orbiting cycle ring and floating glass cards.
 * The cards are illustrative UI, not user data.
 */
export function AuthStage({ isSignup }) {
  usePremiumFonts();
  const rig = useRef(null);
  useTilt(rig);
  return (
    <aside className="fp-astage" aria-label={T("FemCare app preview")}>
      <div className="fp-aurora" aria-hidden="true"><i /><i /><i /></div>
      <div className="fp-stars" aria-hidden="true" />
      <div className="fp-scene" aria-hidden="true">
        <div className="fp-rig" ref={rig}>
          <div className="fp-orbit" />
          <div className="fp-phone"><div><img src="/femcare-ui-v2.jpg" alt="" loading="eager" decoding="async" /></div></div>
          <div className="fp-gcard fp-gc-1"><MiniRing /><small>{T("Next period")}</small><strong>{T("in 24 days")}</strong></div>
          <div className="fp-gcard fp-gc-2"><small>{T("Fertile window")}</small><strong>{T("Estimated · Day 10–15")}</strong><p>{T("Daily insights")}</p></div>
          <div className="fp-gcard fp-gc-3"><span className="fp-alock"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg></span><small>{T("Private by design")}</small><strong>{T("Your data, your control")}</strong></div>
        </div>
      </div>
      <div className="fp-grain" aria-hidden="true" />
      <div className="fp-astage-inner">
        <Link to="/" className="fp-abrand" aria-label="Femcare.life home">
          <img src="/femcare-logo-v2.png" alt="" />
          <span>Femcare<b>.life</b></span>
        </Link>
        <div className="fp-ahead">
          <p className="fp-aeyebrow">{isSignup ? T("Begin your journey") : T("Welcome back")}</p>
          <h2 className="fp-atitle fp-serif">
            {isSignup
              ? <>{T("Your body,")} <em>{T("beautifully")}</em> {T("understood.")}</>
              : <>{T("Your rhythm,")} <em>{T("waiting")}</em> {T("for you.")}</>}
          </h2>
          <p className="fp-asub">{T("Cycle, pregnancy, daughter care, AI wellness and fitness — one private space, the same account on iOS, Android and the web.")}</p>
        </div>
        <div>
          <div className="fp-atrust">
            <span><Check />{T("Private by design")}</span>
            <span><Check />{T("Available in 24 languages")}</span>
            <span><Check />{T("Delete your data any time")}</span>
          </div>
          <p className="fp-afoot" style={{ marginTop: 18 }}>{T("General wellness information, not medical advice.")}</p>
        </div>
      </div>
    </aside>
  );
}

/* Replacement for the legacy AuthCard — same props, premium layout. */
export function PremiumAuthCard({ children, title, subtitle, footer }) {
  return (
    <div className="fp-acard" data-testid="auth-card">
      <div className="fp-acard-top">
        <Link to="/" className="fp-aback" data-testid="auth-back-home">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          {T("Back to femcare.life")}
        </Link>
        <Link to="/" aria-label="Close and return home" data-testid="auth-close-btn" className="fp-aclose">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </Link>
      </div>
      <h1>{title}</h1>
      {subtitle && <p className="fp-asubt">{subtitle}</p>}
      <div className="fp-acard-body">{children}</div>
      {footer && <div className="fp-acard-foot">{footer}</div>}
    </div>
  );
}
